import React from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { TokenAwareTextField as TextField } from './TokenAwareTextField';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useCampaignContentBlocks, useCampaignMutations } from '../../../../hooks/campaign-hooks';
import { Campaign, CampaignContentBlock } from '../../../../support/campaigns';
import { GhostButton, SectionHeader, builderStyles } from './primitives';
import { CATEGORY_SCHEMAS } from './contentBlockSchemas';
import { SchemaPayloadFields } from './SchemaPayloadFields';
import { byOrder, nextOrderOf } from './builderShared';
import type { NestedTreeDef, NodeChildRel } from './nestedContentTrees';

/**
 * Generic, config-driven editor for a *hierarchical* slice of a campaign's content blocks, described by
 * a `NestedTreeDef` (see `nestedContentTrees.ts`). It renders the tree exactly as the runner nests it —
 * e.g. Scenario ▸ Phase ▸ Section ▸ Question — and gives the admin complete control of every element:
 * edit each node's title/key and (via the shared `SchemaPayloadFields`) its payload fields, add / delete
 * / reorder at every level, and toggle visibility. No hierarchy is hard-coded here; a new nested content
 * type is added purely as a `NestedTreeDef`.
 *
 * Child links follow the tree config: `parent` (child.parent === node.id) or `payloadKeys` (child keys
 * listed in `node.payload[field]`). Structural edits maintain whichever linkage applies — reordering a
 * `payloadKeys` child rewrites the key list; deleting a node cascades to its descendants and unlinks it
 * from any owner key list; renaming a key rewrites every key list that referenced it, so links never
 * dangle. Persistence is the same full-record PUT the rest of the builder uses.
 */


const uid = (): string => Math.random().toString(36).slice(2, 7);

export function NestedContentEditor(props: {
  campaign: Campaign;
  canWrite: boolean;
  tree: NestedTreeDef;
}): JSX.Element {
  const { campaign, canWrite, tree } = props;
  const { data: allRows = [], isLoading } = useCampaignContentBlocks(campaign.id);
  const mut = useCampaignMutations('contentBlock');

  const [expanded, setExpanded] = React.useState<Set<number>>(() => new Set());
  const toggle = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Every payload field used as a `payloadKeys` link across the tree — the set the editor hides from a
  // node's scalar fields (it manages them via the child UI instead) and rewrites on a key rename.
  const payloadKeyFields = React.useMemo(() => {
    const s = new Set<string>();
    for (const nd of Object.values(tree.nodes))
      for (const rel of nd.children ?? [])
        if (rel.link.via === 'payloadKeys') s.add(rel.link.field);
    return s;
  }, [tree]);

  const rootDef = tree.nodes[tree.rootType];

  const childrenOf = React.useCallback(
    (block: CampaignContentBlock, rel: NodeChildRel): CampaignContentBlock[] => {
      const cdef = tree.nodes[rel.type];
      if (rel.link.via === 'parent') {
        return byOrder(
          allRows.filter(
            (b) => b.category === cdef.category && String(b.parent) === String(block.id),
          ),
        );
      }
      const keys = Array.isArray((block.payload as any)?.[rel.link.field])
        ? ((block.payload as any)[rel.link.field] as string[])
        : [];
      const byKey = new Map(
        allRows.filter((b) => b.category === cdef.category).map((b) => [b.key, b] as const),
      );
      return keys.map((k) => byKey.get(k)).filter((b): b is CampaignContentBlock => !!b);
    },
    [allRows, tree],
  );

  const rootNodes = React.useMemo(
    () => byOrder(allRows.filter((b) => b.category === rootDef.category && b.parent == null)),
    [allRows, rootDef],
  );

  const save = (row: CampaignContentBlock, next: Partial<CampaignContentBlock>) =>
    mut.update.mutate({ ...row, ...next, id: row.id });

  /** Rename a block's stable key, rewriting every payloadKeys list that referenced the old key. */
  const renameKey = (block: CampaignContentBlock, newKey: string) => {
    const oldKey = block.key;
    if (!newKey || newKey === oldKey) return;
    save(block, { key: newKey });
    for (const b of allRows) {
      if (b.id === block.id) continue;
      const p = { ...((b.payload as Record<string, unknown>) ?? {}) };
      let changed = false;
      for (const f of payloadKeyFields) {
        if (Array.isArray(p[f]) && (p[f] as string[]).includes(oldKey)) {
          p[f] = (p[f] as string[]).map((k) => (k === oldKey ? newKey : k));
          changed = true;
        }
      }
      if (changed) save(b, { payload: p });
    }
  };

  const addChild = (owner: CampaignContentBlock | null, rel: NodeChildRel) => {
    const cdef = tree.nodes[rel.type];
    const sibs = owner ? childrenOf(owner, rel) : rootNodes;
    const ctx = { parent: owner ?? undefined, siblingCount: sibs.length };
    mut.create.mutate(
      {
        campaign: campaign.id,
        parent: rel.link.via === 'parent' && owner ? owner.id : null,
        category: cdef.category,
        key: cdef.makeKey?.(ctx) ?? `${cdef.category}-${uid()}`,
        label: cdef.makeLabel?.(ctx) ?? '',
        order: nextOrderOf(sibs),
        hidden: false,
        payload: cdef.defaultPayload?.() ?? {},
        metadata: {},
      },
      {
        onSuccess: (created: CampaignContentBlock) => {
          if (owner && rel.link.via === 'payloadKeys' && created?.key) {
            const f = rel.link.field;
            const list = Array.isArray((owner.payload as any)?.[f])
              ? [...((owner.payload as any)[f] as string[])]
              : [];
            list.push(created.key);
            save(owner, { payload: { ...(owner.payload as any), [f]: list } });
          }
          if (created && typeof created.id === 'number')
            setExpanded((prev) => new Set(prev).add(created.id));
        },
      },
    );
  };

  /** Reorder the child at `index` by `dir` within `owner`'s children of `rel` (root when owner null). */
  const moveChild = (
    owner: CampaignContentBlock | null,
    rel: NodeChildRel,
    index: number,
    dir: -1 | 1,
  ) => {
    const sibs = owner ? childrenOf(owner, rel) : rootNodes;
    const a = sibs[index];
    const b = sibs[index + dir];
    if (!a || !b) return;
    if (rel.link.via === 'parent' || !owner) {
      save(a, { order: b.order });
      save(b, { order: a.order });
    } else {
      const f = rel.link.field;
      const list = [...(((owner.payload as any)?.[f] as string[]) ?? [])];
      const j = list.indexOf(a.key);
      const k = list.indexOf(b.key);
      if (j < 0 || k < 0) return;
      [list[j], list[k]] = [list[k], list[j]];
      save(owner, { payload: { ...(owner.payload as any), [f]: list } });
    }
  };

  const collectDescendantIds = (block: CampaignContentBlock, nodeType: string, acc: number[]) => {
    const def = tree.nodes[nodeType];
    for (const rel of def.children ?? [])
      for (const child of childrenOf(block, rel)) {
        collectDescendantIds(child, rel.type, acc);
        acc.push(child.id);
      }
  };

  const deleteNode = (
    block: CampaignContentBlock,
    nodeType: string,
    owner: CampaignContentBlock | null,
    ownerRel: NodeChildRel | null,
  ) => {
    const ids: number[] = [];
    collectDescendantIds(block, nodeType, ids);
    ids.push(block.id);
    if (owner && ownerRel?.link.via === 'payloadKeys') {
      const f = ownerRel.link.field;
      const list = (((owner.payload as any)?.[f] as string[]) ?? []).filter((k) => k !== block.key);
      save(owner, { payload: { ...(owner.payload as any), [f]: list } });
    }
    for (const id of ids) mut.remove.mutate(id);
  };

  const renderNode = (
    block: CampaignContentBlock,
    nodeType: string,
    owner: CampaignContentBlock | null,
    ownerRel: NodeChildRel | null,
    index: number,
    total: number,
  ): JSX.Element => {
    const def = tree.nodes[nodeType];
    const schema = CATEGORY_SCHEMAS[def.category];
    const isOpen = expanded.has(block.id);
    const title = block.label || block.key || `(untitled ${def.noun.toLowerCase()})`;
    // Hide any scalar field that this node manages via a child link (e.g. a phase's `sections` list).
    const hiddenFields = new Set(
      (def.children ?? [])
        .map((r) => (r.link.via === 'payloadKeys' ? r.link.field : ''))
        .filter(Boolean),
    );
    const filteredSchema = schema
      ? { ...schema, fields: schema.fields.filter((f) => !hiddenFields.has(f.key)) }
      : undefined;

    return (
      <Box
        key={block.id}
        sx={{
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 1,
          opacity: block.hidden ? 0.55 : 1,
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ px: 1, py: 0.5, minWidth: 0, cursor: 'pointer' }}
          onClick={() => toggle(block.id)}
        >
          <IconButton size="small" sx={{ p: 0.25 }}>
            {isOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
          </IconButton>
          <Chip size="small" label={def.noun} sx={{ height: 20 }} />
          <Typography variant="body2" noWrap sx={{ fontWeight: 600, minWidth: 0, flexShrink: 1 }}>
            {title}
          </Typography>
          {canWrite && (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ ml: 'auto' }}>
              <GhostButton
                onClick={(e) => {
                  e.stopPropagation();
                  moveChild(owner, ownerRel as NodeChildRel, index, -1);
                }}
                disabled={index === 0 || mut.isSaving || !ownerRel}
              >
                ↑
              </GhostButton>
              <GhostButton
                onClick={(e) => {
                  e.stopPropagation();
                  moveChild(owner, ownerRel as NodeChildRel, index, 1);
                }}
                disabled={index === total - 1 || mut.isSaving || !ownerRel}
              >
                ↓
              </GhostButton>
              <Tooltip title={block.hidden ? 'Hidden — show' : 'Hide from consumers'}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    save(block, { hidden: !block.hidden });
                  }}
                >
                  {block.hidden ? (
                    <VisibilityOffIcon fontSize="small" />
                  ) : (
                    <VisibilityIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip title={`Delete this ${def.noun.toLowerCase()} and everything under it`}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteNode(block, nodeType, owner, ownerRel);
                  }}
                  disabled={mut.isSaving}
                  aria-label={`Remove ${def.noun}`}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </Stack>

        <Collapse in={isOpen} unmountOnExit>
          <Box sx={{ px: 1.5, pb: 1.5, pt: 0.5 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
              <TextField
                label={`${def.noun} title`}
                size="small"
                fullWidth
                defaultValue={block.label}
                disabled={!canWrite}
                helperText="The on-screen title (block Label)"
                onBlur={(e) => e.target.value !== block.label && save(block, { label: e.target.value })}
              />
              <TextField
                label="Key"
                size="small"
                defaultValue={block.key}
                disabled={!canWrite}
                helperText="Stable id (links update automatically)"
                onBlur={(e) => renameKey(block, e.target.value)}
                sx={{ minWidth: 200, fontFamily: 'monospace' }}
              />
            </Stack>

            {filteredSchema && (
              <SchemaPayloadFields
                block={block}
                schema={filteredSchema}
                canWrite={canWrite}
                campaignId={campaign.id}
                onSavePayload={(payload) => save(block, { payload })}
              />
            )}

            {(def.children ?? []).map((rel) => {
              const cdef = tree.nodes[rel.type];
              const kids = childrenOf(block, rel);
              return (
                <Box key={rel.type} sx={{ mt: 1.5, pl: 1.5, borderLeft: '2px solid', borderColor: 'divider' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {cdef.noun}s
                  </Typography>
                  <Stack spacing={1} sx={{ mt: 0.75 }}>
                    {kids.length === 0 && (
                      <Typography variant="caption" color="text.secondary">
                        No {cdef.noun.toLowerCase()}s yet.
                      </Typography>
                    )}
                    {kids.map((child, i) => renderNode(child, rel.type, block, rel, i, kids.length))}
                  </Stack>
                  {canWrite && (
                    <Box sx={{ mt: 0.75 }}>
                      <GhostButton onClick={() => addChild(block, rel)} disabled={mut.isSaving}>
                        + {rel.addLabel ?? `Add ${cdef.noun.toLowerCase()}`}
                      </GhostButton>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <div className={builderStyles.contentCard}>
      <SectionHeader compact title={tree.title} subtitle={tree.subtitle} />
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={22} />
        </Box>
      ) : (
        <Stack spacing={1}>
          {rootNodes.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No {rootDef.noun.toLowerCase()}s yet.
            </Typography>
          )}
          {rootNodes.map((block, i) =>
            renderNode(block, tree.rootType, null, null, i, rootNodes.length),
          )}
          {canWrite && (
            <Box>
              <GhostButton
                onClick={() => addChild(null, { type: tree.rootType, link: { via: 'parent' } })}
                disabled={mut.isSaving}
              >
                + {tree.addRootLabel}
              </GhostButton>
            </Box>
          )}
        </Stack>
      )}
    </div>
  );
}

export default NestedContentEditor;
